/**
 * Main App component with React Router configuration.
 * Features route-based code splitting for better initial load performance.
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ProtectedRoute } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { VerticalProvider } from '@/contexts/VerticalContext';
import { ErrorBoundary, Layout } from '@/components';
import { ToastProvider } from '@/components/ui';
import { ProtectedAdminRoute } from '@/components/admin';

// Route-based code splitting with React.lazy()
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AgentsPage = lazy(() => import('@/pages/AgentsPage').then(m => ({ default: m.AgentsPage })));
const AgentDetailPage = lazy(() => import('@/pages/AgentDetailPage').then(m => ({ default: m.AgentDetailPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const BillingPage = lazy(() => import('@/pages/BillingPage').then(m => ({ default: m.BillingPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })));

// Loading fallback component for lazy-loaded routes
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <VerticalProvider>
            <ThemeProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AuthProvider>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/terms" element={<TermsPage />} />

                  {/* Protected routes with Layout */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SettingsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <BillingPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedAdminRoute>
                        <Layout>
                          <AdminPage />
                        </Layout>
                      </ProtectedAdminRoute>
                    }
                  >
                    {/* Default tab - redirect to overview */}
                    <Route index element={<Navigate to="/admin/overview" replace />} />
                    {/* Admin sub-routes enable URL-based tab navigation.
                     * element={null} is used because AdminPage handles rendering
                     * based on the URL path. The routes exist to match URLs and
                     * enable browser history, not to render separate components.
                     */}
                    {/* Admin sub-routes for each tab */}
                    <Route path="overview" element={null} />
                    <Route path="agencies" element={null} />
                    <Route path="users" element={null} />
                    <Route path="resources" element={null} />
                    <Route path="agents" element={null} />
                    <Route path="ai-agents" element={null} />
                    <Route path="health" element={null} />
                    <Route path="ota-campaigns" element={null} />
                    <Route path="credentials" element={null} />
                    <Route path="audit" element={null} />
                    <Route path="usage" element={null} />
                    <Route path="billing" element={null} />
                    <Route path="openrouter-spend" element={null} />
                    <Route path="agency-limits" element={null} />
                  </Route>

                  {/* Redirect root to dashboard or login */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute redirectTo="/login">
                        <Navigate to="/dashboard" replace />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Not Found */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </ThemeProvider>
        </VerticalProvider>
      </ToastProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
